import { createId } from './ids';
import { cloneLayerTree, findLayerById, isGroup, nextLayerNameInTree } from './layerTree';
import type { ShapeGeometry, ShapeStroke } from '../vector/types';
import type {
  AdjustmentLayer,
  AdjustmentState,
  DocumentMeta,
  FillContent,
  FillLayer,
  GroupLayer,
  Layer,
  RasterLayer,
  RGBA,
  ShapeLayer,
  ShapePaint,
  SmartLayer,
  TextLayer,
} from './types';

const LAYER_DEFAULTS = {
  visible: true,
  opacity: 1,
  blendMode: 'normal' as const,
  blendChannels: { r: true, g: true, b: true, a: true },
  blendIf: {
    channel: 'gray' as const,
    thisLayer: { black0: 0, black1: 0, white0: 255, white1: 255 },
    underlying: { black0: 0, black1: 0, white0: 255, white1: 255 },
  },
  locked: false,
  lockPixels: false,
  lockPosition: false,
  lockMask: false,
  hasMask: false,
  maskEnabled: true,
  maskLinked: true,
  clipping: false,
  vectorMask: null,
  effects: null,
  contentOffset: { x: 0, y: 0 },
};

export function createLayer(name: string, overrides: Partial<RasterLayer> = {}): RasterLayer {
  return { kind: 'raster', id: createId('layer'), name, ...LAYER_DEFAULTS, ...overrides };
}

export function createGroupLayer(
  name: string,
  children: Layer[] = [],
  overrides: Partial<GroupLayer> = {},
): GroupLayer {
  return {
    kind: 'group',
    id: createId('group'),
    name,
    ...LAYER_DEFAULTS,
    children,
    expanded: true,
    ...overrides,
  };
}

export function createAdjustmentLayer(
  name: string,
  adjustment: AdjustmentState,
  overrides: Partial<AdjustmentLayer> = {},
): AdjustmentLayer {
  return {
    kind: 'adjustment',
    id: createId('adjust'),
    name,
    ...LAYER_DEFAULTS,
    adjustment,
    ...overrides,
  };
}

export function createFillLayer(
  name: string,
  fill: FillContent,
  overrides: Partial<FillLayer> = {},
): FillLayer {
  return {
    kind: 'fill',
    id: createId('fill'),
    name,
    ...LAYER_DEFAULTS,
    fill,
    ...overrides,
  };
}

export function createShapeLayer(
  name: string,
  geometry: ShapeGeometry,
  fill: ShapePaint,
  stroke: (ShapeStroke & { color: RGBA }) | null,
  overrides: Partial<ShapeLayer> = {},
): ShapeLayer {
  return {
    kind: 'shape',
    id: createId('shape'),
    name,
    ...LAYER_DEFAULTS,
    geometry,
    fill,
    stroke,
    ...overrides,
  };
}

export function createTextLayer(
  name: string,
  overrides: Partial<TextLayer> = {},
): TextLayer {
  return {
    kind: 'text',
    id: createId('text'),
    name,
    ...LAYER_DEFAULTS,
    content: 'Text',
    fontFamily: 'Arial',
    fontSize: 48,
    fontWeight: 'normal',
    fontStyle: 'normal',
    align: 'left',
    color: { r: 0, g: 0, b: 0, a: 1 },
    x: 0,
    y: 0,
    ...overrides,
  };
}

export function createSmartLayer(
  name: string,
  overrides: Partial<SmartLayer> = {},
): SmartLayer {
  return {
    kind: 'smart',
    id: createId('smart'),
    name,
    ...LAYER_DEFAULTS,
    smartFilters: [],
    ...overrides,
  };
}

export function createDocumentMeta(params: {
  name: string;
  width: number;
  height: number;
  layers?: Layer[];
}): DocumentMeta {
  const layers = params.layers?.length ? params.layers : [createLayer('Layer 1')];
  return {
    id: createId('doc'),
    name: params.name,
    width: params.width,
    height: params.height,
    layers,
    activeLayerId: layers[layers.length - 1].id,
    hasSelection: false,
    channels: [],
    colorVisible: { r: true, g: true, b: true },
    paths: [],
    activePathId: null,
    guides: [],
    dirty: false,
  };
}

export function findLayer(doc: DocumentMeta, layerId: string | null): Layer | null {
  return findLayerById(doc.layers, layerId);
}

export function getActiveLayer(doc: DocumentMeta): Layer | null {
  return findLayer(doc, doc.activeLayerId);
}

/** Returns a name that is not already used in the document, e.g. `Layer 3`. */
export function nextLayerName(doc: DocumentMeta, base = 'Layer'): string {
  return nextLayerNameInTree(doc.layers, base);
}

export function cloneDocumentMeta(doc: DocumentMeta): DocumentMeta {
  return {
    ...doc,
    layers: doc.layers.map(cloneLayerBranch),
    guides: doc.guides.map((guide) => ({ ...guide })),
  };
}

/** Structural copy that keeps ids, for history snapshots. */
function cloneLayerBranch(layer: Layer): Layer {
  if (!isGroup(layer)) return { ...layer };
  return { ...layer, children: layer.children.map(cloneLayerBranch) };
}

/** Fresh-id copy of a layer or a whole group, for Duplicate. */
export function duplicateLayerTree(layer: Layer): Layer {
  return cloneLayerTree(layer, () => createId(layer.kind === 'group' ? 'group' : 'layer'));
}
